"use client";

import { useEffect, useState } from "react";

interface Particle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  speedX: number;
  speedY: number;
  life: number;
}

interface Firework {
  id: number;
  x: number;
  y: number;
  particles: Particle[];
}

const colors = [
  "#22c55e", // green-500
  "#4ade80", // green-400
  "#86efac", // green-300
  "#fbbf24", // amber-400
  "#f59e0b", // amber-500
  "#34d399", // emerald-400
];

export function Fireworks() {
  const [fireworks, setFireworks] = useState<Firework[]>([]);

  useEffect(() => {
    const createFirework = () => {
      const id = Date.now() + Math.random();
      const x = 10 + Math.random() * 80; // 10-90% of width
      const y = 20 + Math.random() * 40; // 20-60% of height
      
      const particles: Particle[] = [];
      const particleCount = 16 + Math.floor(Math.random() * 12);
      
      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount;
        const speed = 2.5 + Math.random() * 4;
        particles.push({
          id: i,
          x: 0,
          y: 0,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 4 + Math.random() * 4,
          speedX: Math.cos(angle) * speed,
          speedY: Math.sin(angle) * speed,
          life: 1,
        });
      }

      setFireworks((prev) => [...prev, { id, x, y, particles }]);

      // Remove firework after animation
      setTimeout(() => {
        setFireworks((prev) => prev.filter((f) => f.id !== id));
      }, 1500);
    };

    // Create initial burst of fireworks
    createFirework();
    setTimeout(createFirework, 100);
    setTimeout(createFirework, 200);
    setTimeout(createFirework, 350);
    setTimeout(createFirework, 500);

    // Continue creating fireworks more frequently
    const interval = setInterval(() => {
      createFirework();
      // Sometimes launch two at once
      if (Math.random() > 0.5) {
        setTimeout(createFirework, 100);
      }
    }, 300 + Math.random() * 300);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {fireworks.map((firework) => (
        <div
          key={firework.id}
          className="absolute"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
          }}
        >
          {firework.particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute animate-firework-particle rounded-full"
              style={{
                width: particle.size,
                height: particle.size,
                backgroundColor: particle.color,
                boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
                "--tx": `${particle.speedX * 40}px`,
                "--ty": `${particle.speedY * 40}px`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
